// Phase 7 R4 — list returns (with nested lines) for a PO.
// Phase 7 R5 — also resolves creator display name (read-only) for history card.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PartsReturnLine {
  id: string;
  parts_return_id: string;
  purchase_order_line_id: string | null;
  parts_line_id: string | null;
  shop_id: string;
  quantity_returned: number;
  unit_credit: number | null;
  reason: string | null;
  created_at: string;
}

export interface PartsReturn {
  id: string;
  shop_id: string;
  purchase_order_id: string | null;
  return_reference: string | null;
  status: string;
  reason: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  parts_return_lines: PartsReturnLine[];
}

export function usePurchaseOrderReturns(poId: string | undefined) {
  return useQuery({
    queryKey: ['po-returns', poId],
    enabled: !!poId,
    queryFn: async (): Promise<PartsReturn[]> => {
      const { data, error } = await (supabase as any)
        .from('parts_returns')
        .select('*, parts_return_lines(*)')
        .eq('purchase_order_id', poId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const rows = (data ?? []) as Array<Omit<PartsReturn, 'created_by_name'>>;
      const ids = Array.from(new Set(rows.map((r) => r.created_by).filter((x): x is string => !!x)));

      let nameById = new Map<string, string>();
      if (ids.length > 0) {
        const { data: profiles } = await (supabase as any)
          .from('profiles')
          .select('id, full_name, email')
          .in('id', ids);
        nameById = new Map(
          (profiles ?? []).map((p: any) => [
            p.id as string,
            (p.full_name as string | null) || (p.email as string | null) || 'Unknown user',
          ]),
        );
      }

      return rows.map((r) => ({
        ...r,
        created_by_name: r.created_by ? nameById.get(r.created_by) ?? 'Unknown user' : null,
      }));
    },
  });
}
